import { useState } from "react";
import { MD } from "../components/Markdown";

import welcomeMd            from "./content/00-welcome.md?raw";
import thesisMd             from "./content/01-thesis.md?raw";
import architectureMd       from "./content/02-architecture.md?raw";
import noServerMultiMd      from "./content/03-no-server-multiplayer.md?raw";
import channelByokMd        from "./content/04-channel-byok.md?raw";
import toolCallingMd        from "./content/05-tool-calling.md?raw";
import designMd             from "./content/06-design.md?raw";
import extensionPointsMd    from "./content/07-extension-points.md?raw";
import brainstormMd         from "./content/08-brainstorm.md?raw";
import libraryMd            from "./content/09-library.md?raw";
import templatesMd          from "./content/10-templates.md?raw";

// Wiki — second top-level view of the Playground. Same titlebar, same
// brand register; the channel sidebar yields to a section/page index
// when the user toggles into Wiki mode. Content lives in content/*.md
// as raw imports — drop a new file, add it to SECTIONS, ship.

interface Page {
  id: string;
  title: string;
  body: string;
}

interface Section {
  title: string;
  pages: Page[];
}

const SECTIONS: Section[] = [
  {
    title: "start here",
    pages: [
      { id: "welcome", title: "Welcome",          body: welcomeMd },
      { id: "thesis",  title: "The thesis",       body: thesisMd },
    ],
  },
  {
    title: "architecture",
    pages: [
      { id: "arch",     title: "Architecture",            body: architectureMd },
      { id: "sync",     title: "No-server multiplayer",   body: noServerMultiMd },
      { id: "byok",     title: "Channel-owned BYOK",      body: channelByokMd },
      { id: "tools",    title: "Tool calling (ReAct)",    body: toolCallingMd },
    ],
  },
  {
    title: "modes",
    pages: [
      { id: "brainstorm", title: "Brainstorm mode",       body: brainstormMd },
      { id: "library",    title: "Document library",      body: libraryMd },
      { id: "templates",  title: "Channel templates",     body: templatesMd },
    ],
  },
  {
    title: "design",
    pages: [
      { id: "design",   title: "Visual register",         body: designMd },
    ],
  },
  {
    title: "extending",
    pages: [
      { id: "extend",   title: "Extension points",        body: extensionPointsMd },
    ],
  },
];

const ALL_PAGES = SECTIONS.flatMap((s) => s.pages);

interface WikiViewProps {
  /** Pre-select a wiki page on mount — used by deep-links from
   *  feature affordances (e.g. the Brainstorm toolbar's ?-button
   *  jumps straight to the brainstorm page). Falls back to "welcome"
   *  when the id doesn't match any known page. */
  initialId?: string;
}

export function WikiView({ initialId }: WikiViewProps = {}) {
  const [activeId, setActiveId] = useState<string>(
    initialId && ALL_PAGES.some((p) => p.id === initialId) ? initialId : "welcome",
  );
  const active = ALL_PAGES.find((p) => p.id === activeId) ?? ALL_PAGES[0];

  return (
    <div className="wiki-route">
      <aside className="wiki-sidebar">
        {SECTIONS.map((sec) => (
          <div key={sec.title} className="wiki-section">
            <div className="wiki-section-head">{sec.title}</div>
            {sec.pages.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`wiki-link ${p.id === activeId ? "active" : ""}`}
                onClick={() => setActiveId(p.id)}
              >
                {p.title}
              </button>
            ))}
          </div>
        ))}
        <div className="wiki-section">
          <div className="wiki-section-head">extend</div>
          <div className="wiki-side-note">
            pages live in <code>src/wiki/content/</code>. drop a <code>.md</code> file, add it to the manifest in <code>src/wiki/index.tsx</code>, ship.
          </div>
        </div>
      </aside>
      <article className="wiki-article" key={active.id}>
        <MD>{active.body}</MD>
      </article>
    </div>
  );
}
